/** Categorical series colours shared by every ApexCharts chart in the admin. */
export const CHART_COLOR_PALETTE = [
  '#fc0090',
  '#1d4ed8',
  '#0ea5a4',
  '#f59e0b',
  '#8b5cf6',
  '#14b8a6',
  '#ef4444',
  '#334155',
  '#db2777',
  '#16a34a',
];

/** Palette entries that read almost the same as an earlier one (two teals, two pinks). */
const NEAR_DUPLICATES = new Set(['#14b8a6', '#db2777']);

const GOLDEN_ANGLE = 137.508;

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return [Math.round(f(0) * 255), Math.round(f(8) * 255), Math.round(f(4) * 255)];
}

function toHex([r, g, b]: [number, number, number]): string {
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

function fromHex(hex: string): [number, number, number] {
  return [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)) as [number, number, number];
}

function distance(a: [number, number, number], b: [number, number, number]): number {
  return Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
}

/**
 * Exactly `count` colours for a categorical chart where every series must be
 * told apart (one per night): the palette first (minus its near
 * duplicates), then colours generated by stepping the hue by the golden angle
 * at a fixed saturation, with lightness alternating by lap so it reads on both
 * light and dark backgrounds. A candidate too close to a colour already chosen
 * is skipped (the tolerance relaxes slowly, so any count terminates), so no
 * colour repeats. The shared `CHART_COLOR_PALETTE` itself is unchanged.
 */
export function seriesColors(count: number): string[] {
  const colors = CHART_COLOR_PALETTE.filter((c) => !NEAR_DUPLICATES.has(c)).slice(0, count);
  const rgbs = colors.map(fromHex);
  let tolerance = 70;
  let step = 0;
  while (colors.length < count) {
    const hue = (step * GOLDEN_ANGLE) % 360;
    const lightness = 0.5 + 0.08 * Math.sin(step * 0.9);
    const rgb = hslToRgb(hue, 0.68, lightness);
    const hex = toHex(rgb);
    step += 1;
    if (step % 200 === 0) tolerance *= 0.85;
    if (rgbs.some((o) => distance(o, rgb) < tolerance)) continue;
    colors.push(hex);
    rgbs.push(rgb);
  }
  return colors;
}
